import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { fetchAppSettings, GetStreamChatToken } from "../services/api";
import Unauthorized from "../components/Unauthorized";
import EventScribeSpinner from "../components/shared/EventScribeSpinner";
import { getChatClient } from "../utils/streamChatClient";
const AppSettingsContext = createContext();
import { useAppEnv } from '../context/AppEnvContext';
import { useAuth } from "../context/AuthContext";
import { API_URLS } from "../constants/apiUrls";
export const AppSettingsProvider = ({
  token,
  children,
}) => {
  const env = useAppEnv();
  const { authData } = useAuth();
  const _guard_ = authData._guard_;
  const systemToken = authData.systemToken;
  const [settings, setSettings] = useState({
    backgroundColor: "#ffffff",
    textColor: "#333333",
    buttonColor: "#0077cc",
    enableAppointment:0,
  });
  const [tokens, setTokens] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [chatClient, setChatClient] = useState(null);
  const [eventTimezone, setEventTimezone] = useState('America/New_York');
  const setToken = (userId, token) => {
    setTokens((prevTokens) => {
      if (prevTokens[userId] === token) return prevTokens; // Avoid unnecessary updates
      return { ...prevTokens, [userId]: token };
    });
  };
  // Memoize the StreamChat client
  const memoizedChatClient = useMemo(() => getChatClient(env.REACT_APP_STREAM_KEY), []);
  // Use useRef to persist the isConnecting flag across renders
  const isConnecting = useRef(false);
  useEffect(() => {
    const initialize = async () => {
      try {
        // Use _guard_ and systemToken from context, not from StorageService
        if (!_guard_ || !systemToken) {
          throw new Error("Invalid request");
          }  

          setChatClient(memoizedChatClient); // Set the client in state
          // Replace {0} in API_URLS.BANNER with eventId
          const bannerUrl = API_URLS.BANNER.replace("{0}", authData.eventId);
        // Fetch app settings
          const appSettings = await fetchAppSettings(bannerUrl, systemToken);

        setEventTimezone(appSettings.tZone || 'America/New_York');
        setSettings({
          bgColor: appSettings.navBackgroundColor,
          textColor: appSettings.navForegroundColor,
          buttonColor: appSettings.loginButtonColor,
          enableAppointment:appSettings.hideAppointmentFeature,
        });


        // Manage _stream_ token in memory only
        let streamToken = tokens[`_stream_${_guard_}`];
        if (!streamToken) {        
            // Pass systemToken from context
            const response = await GetStreamChatToken(API_URLS.STREAM_TOKEN, systemToken);
          streamToken = response.token;
          setToken(`_stream_${_guard_}`, streamToken);
        }


        // Connect user only if not already connected
        if (!memoizedChatClient.userID && !isConnecting.current) {
          isConnecting.current = true; // Set the flag to true
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Add delay

          isConnecting.current = false; // Reset the flag
        }

        setLoading(false);
      } catch (err) {
        console.error("Error initializing AppSettingsContext:", err);
        setError(true);
        setLoading(false);
      }
    };

    initialize();
    return () => {
      if (memoizedChatClient) memoizedChatClient.disconnectUser(); // Clean up on unmount
    };
  }, [token, memoizedChatClient]);

  if (loading) {
    return <EventScribeSpinner logovisible={true} />;
  }

  if (error) {
    return <Unauthorized />;
  }

  return (
    <AppSettingsContext.Provider value={{ settings, tokens, chatClient, eventTimezone }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
